import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef, useImperativeHandle, useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import { CompGroup } from "userful-chronos-app-common-js/dist/models/group/Groups";
import { useTranslation } from "react-i18next";
import UserfulConfirmationDialog from "../../ConfirmationDialog/UserfulConfirmationDialog";
import { Button, Input, UserfulTooltip } from "../..";
import { faCircleExclamation, faTrashAlt } from "@fortawesome/pro-regular-svg-icons";
import SmallOptionContainer from "../../option/smallOptionContainer/SmallOptionContainer";
import { TOptionValue } from "../../option/optionsUtils";

interface IProps {
    folders: CompGroup[];
    type: "dropdown" | "button";
    onDelete: (id: string, recursiveDelete?: boolean) => void;
    addiotnalSecurity?: boolean;
    hideTrigger?: boolean;
}

export type DeleteFolderHandle = {
    open: () => void;
    close: () => void;
};

const DeleteFolder = forwardRef<DeleteFolderHandle, IProps>(function DeleteFolder(props, ref) {
    const { t } = useTranslation();

    const [show, setShow] = useState(false);
    const [input, setInput] = useState<string>("");
    const [recursive, setRecursive] = useState<TOptionValue>("unselected");

    if (props.folders.length === 0) return null;

    const additionalfolders = props.folders.length - 1;
    const addiotnalSecurity = props.addiotnalSecurity ? true : false;

    useImperativeHandle(ref, () => ({
        open: () => setShow(true),
        close: () => setShow(false),
    }));

    return (
        <>
            {!props.hideTrigger && props.type === "dropdown" && (
                <Dropdown.Item onClick={(e) => setShow(true)}>
                    {additionalfolders > 1 && (
                        <div className="userful-icon-dropdown-menu-icon-container">
                            <FontAwesomeIcon icon={faTrashAlt} />
                        </div>
                    )}
                    <p>{additionalfolders > 1 ? t("Shared.deleteAll") : t("Shared.delete")}</p>
                </Dropdown.Item>
            )}

            {!props.hideTrigger && props.type === "button" && (
                <UserfulTooltip title={additionalfolders > 1 ? t("Shared.deleteAll") : t("Shared.delete")}>
                    <Button variant="secondary" iconOnly onClick={() => setShow(true)}>
                        <FontAwesomeIcon icon={faTrashAlt} />
                    </Button>
                </UserfulTooltip>
            )}

            <UserfulConfirmationDialog
                show={show}
                icon={{ icon: faCircleExclamation, color: "#D13E2C" }}
                onCancel={() => setShow(false)}
                title={t("Shared.delete") + " " + additionalfolders ? t("Shared.folders") : t("Shared.alert")}
                replaceBody={
                    <>
                        <p>
                            {t("Shared.deleteStart")} <strong>{props.folders[0].name}</strong>
                            {additionalfolders > 0 && (
                                <strong className="userful-tag">+{additionalfolders}</strong>
                            )}? {t("Shared.deleteEnd")}
                        </p>
                        <SmallOptionContainer
                            id="recursive-delete"
                            type={"checkbox"}
                            title={t("Folder.recursiveDelete")}
                            value={recursive}
                            onClick={() => {
                                setRecursive(recursive === "selected" ? "unselected" : "selected");
                            }}
                        />
                        {addiotnalSecurity && (
                            <>
                                <Input
                                    filedTitle={t("Shared.deleteConfirmation")}
                                    value={input}
                                    onChange={(e) => {
                                        setInput(e.target.value);
                                    }}
                                />
                            </>
                        )}
                    </>
                }
                replaceFooter={
                    <>
                        <Button variant="secondary" onClick={() => setShow(false)}>
                            {t("Shared.cancel")}
                        </Button>
                        <Button
                            variant="danger"
                            disabled={addiotnalSecurity && input !== "DELETE"}
                            onClick={() => {
                                props.onDelete(props.folders[0].id.value, recursive === "selected");
                                setShow(false);
                            }}
                        >
                            {additionalfolders > 0 ? t("Shared.deleteAll") : t("Shared.delete")}
                        </Button>
                    </>
                }
            />
        </>
    );
});

export default DeleteFolder;
